import { useState, useEffect } from "react";
import Image from "next/image";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Author } from "@/types";

const AuthorCard = ({ authors }: { authors: Author[] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const cardsPerView = isMobile ? 2 : 3; // 🔹 2 cartes sur mobile, 3 sur desktop
  const totalSlides = Math.ceil(authors.length / cardsPerView);

  // ✅ Vérifie si on est sur mobile
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // ✅ Autoplay si plus de 2 auteurs
  useEffect(() => {
    if (authors.length > 2) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1 < totalSlides ? prev + 1 : 0));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [totalSlides, authors.length]);

  if (!authors || authors.length === 0) return null;

  const title =
    authors.length > 1 ? "À propos des auteurs" : "À propos de l'auteur";

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1 < totalSlides ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 >= 0 ? prev - 1 : totalSlides - 1));
  };

  return (
    <div className="relative mt-10 border-t border-gray-300 pt-6 dark:border-gray-700">
      <h2 className="mb-4 text-lg font-bold md:text-xl">{title}</h2>

      {/* 🔹 Cas 1 : 1 seul auteur (affichage simple) */}
      {authors.length === 1 && <SingleAuthorCard author={authors[0]} />}

      {/* 🔹 Cas 2 : 2+ auteurs → Carrousel sur mobile, affichage en ligne sur desktop */}
      {authors.length > 1 && (
        <div
          className={`relative overflow-hidden ${isMobile ? "block" : "grid grid-cols-3 gap-6"}`}
        >
          {/* 🔹 Mobile → Carrousel */}
          {isMobile ? (
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {authors.map((author, index) => (
                <div key={index} className="w-full flex-shrink-0 p-2 sm:w-1/2">
                  <SingleAuthorCard author={author} />
                </div>
              ))}
            </div>
          ) : (
            // 🔹 Desktop → Affichage en ligne (pas de carrousel)
            authors.map((author, index) => (
              <div key={index} className="w-full">
                <SingleAuthorCard author={author} />
              </div>
            ))
          )}

          {/* 🔹 Boutons de navigation uniquement en mode carrousel */}
          {isMobile && authors.length > 2 && (
            <>
              <button
                className="absolute left-0 top-1/2 -translate-y-1/2 transform rounded-full bg-gray-200 p-2 shadow-md hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                onClick={prevSlide}
              >
                <FaChevronLeft size={24} />
              </button>
              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 transform rounded-full bg-gray-200 p-2 shadow-md hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                onClick={nextSlide}
              >
                <FaChevronRight size={24} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SingleAuthorCard = ({ author }: { author: any }) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(!expanded);

  const previewLength = 100;
  const isLongText = author.description.length > previewLength;
  const previewText = isLongText
    ? author.description.slice(0, previewLength) + "..."
    : author.description;

  return (
    <div className="flex flex-col items-center rounded-lg border bg-gray-100 p-6 text-center shadow-md dark:bg-gray-800">
      <Image
        src={author.avatar_url || "/default-avatar.png"}
        alt={author.name}
        width={64}
        height={64}
        className="mb-3 h-16 w-16 rounded-full"
      />
      <h3 className="text-md font-semibold md:text-lg">{author.name}</h3>
      <p className="text-sm md:text-base">
        {expanded ? author.description : previewText}
      </p>
      {isLongText && (
        <button
          onClick={toggleExpanded}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          {expanded ? "Lire moins" : "Lire la suite"}
        </button>
      )}
    </div>
  );
};

export default AuthorCard;
